import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import Exporting from "highcharts/modules/exporting";
import OfflineExporting from "highcharts/modules/offline-exporting";
import React, { FC } from "react";

const moment = require("moment");
Exporting(Highcharts);
OfflineExporting(Highcharts);

interface CustomSplineChartProps {
  xAxisTitleText: string;
  xAxisCategories?: string[] | null;
  yAxisTitleText: string;
  seriesData: number[];
  baseline?: number[];
  savedEnergy?: number[];
  fileName?: string;
  chartName?: string;
}

const createTooltipFormatter = (
  xAxisTitleText: string,
  yAxisTitleText: string,
  savedEnergy?: number[],
) => function (this: Highcharts.TooltipFormatterContextObject) {
  const xValue = this.x;
  const yValue = this.y;
  let savedPercentage = null;

  if (savedEnergy?.length) {
    savedPercentage = savedEnergy[this.point.x];
  }

  return `
    <strong> ${xAxisTitleText}:</strong> ${xValue}<br><strong>${yAxisTitleText}:</strong> ${yValue}
    ${savedPercentage !== null
      ? `<br><strong>Saved Energy:</strong> ${savedPercentage}%`
      : ""
    }
  `;
};

const CustomSplineChart: FC<CustomSplineChartProps> = ({
  xAxisTitleText,
  xAxisCategories = null,
  yAxisTitleText,
  seriesData,
  baseline,
  savedEnergy,
  fileName,
  chartName,
}) => {
  const getLastSevenDays = () => {
    const lastSevenDays = Array.from({ length: 7 }, (_, index) => {
      const date = moment().subtract(7 - index, "days");
      return date.format("DD-MM-YYYY");
    });
    return lastSevenDays;
  };

  const lastSevenDays = getLastSevenDays();

  // set chart min/max value (including baseline) on y-axis

  // const [yAxisMax, setYAxisMax] = useState<number>(50);
  // const [yAxisMin, setYAxisMin] = useState<number>(0);

  /* useEffect(() => {
    if (baseline?.length && seriesData?.length) {
      let minValue = Math.min(...seriesData, ...baseline) - 5;

      if (minValue < 0) {
        minValue = 0;
      }
      setYAxisMin(minValue);

      let maxValue = Math.max(...seriesData, ...baseline) + 5;
      setYAxisMax(maxValue);
    } else if (seriesData?.length) {
      let minValue = Math.min(...seriesData) - 5;

      if (minValue < 0) {
        minValue = 0;
      }
      setYAxisMin(minValue);

      let maxValue = Math.max(...seriesData) + 5;
      setYAxisMax(maxValue);
    }
  }, [seriesData, baseline]); */

  const chartOptions = {
    chart: {
      // type: "spline",
      style: {
        fontFamily: '"Cabin", Helvetica, Arial, sans-serif',
      },
      lang: {
        noData: "No data available",
      },
      animation: {
        duration: 1500,
      },
      zooming: {
        type: "xy",
      },
    },
    title: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    xAxis: {
      categories: xAxisCategories || lastSevenDays,
      accessibility: {
        description: "Months of the year",
      },
      title: {
        text: xAxisTitleText,
        style: {
          fontSize: "16px",
          fontWeight: "bold",
        },
      },
    },
    yAxis: {
      min: 0,
      tickAmount: null,
      title: {
        text: yAxisTitleText,
        style: {
          fontSize: "16px",
          fontWeight: "bold",
        },
      },
      labels: {
        format: "{value}",
      },
      plotLines: [
        // {
        //   color: "red",
        //   dashStyle: "solid",
        //   value: baseline,
        //   width: 2,
        //   label: {
        //     text: "Baseline",
        //     align: "center",
        //     style: {
        //       color: "gray",
        //     },
        //   },
        // },
      ],
    },
    tooltip: {
      formatter: createTooltipFormatter(
        xAxisTitleText,
        yAxisTitleText,
        savedEnergy,
      ),
    },
    plotOptions: {
      spline: {
        marker: {
          radius: 4,
          lineColor: "#666666",
          lineWidth: 1,
        },
      },
    },
    legend: {
      enabled: true,
      x: 40, // This is added for perfectly aligning legend with y-axis title
      y: 0,
    },
    exporting: {
      enabled: true,
      fallbackToExportServer: false,
      filename: fileName || "chart",
      sourceWidth: 1200, // Increase source dimensions
      sourceHeight: 500,
      scale: 1, // Maintain original scale
      chartOptions: chartName
        ? {
          chart: {
            height: "100%", // Use relative height
            width: "100%", // Use relative width
            spacingTop: 30, // Add more space at the top for the title
            marginTop: 100, // Increase the top margin to push chart content down
          },
          title: {
            text: chartName, // Dynamic title from prop
            align: "center",
            margin: 20,
            style: {
              fontSize: "24px",
              fontWeight: "bold",
            },
          },
        }
        : {
          chart: {
            height: "100%", // Use relative height
            width: "100%", // Use relative width
          },
          title: {
            style: {
              fontSize: "14px",
            },
          },
        },
      buttons: {
        contextButton: {
          menuItems: [
            "viewFullscreen",
            "printChart",
            "separator",
            "downloadPNG",
            "downloadJPEG",
            "downloadPDF",
            "downloadSVG",
          ],
        },
      },
    },
    series:
      seriesData?.length || baseline?.length
        ? [
          {
            type: "spline",
            data: seriesData,
            showInLegend: true,
            name: "Energy Consumption",
            color: "#66b2ff",
          },
          ...(baseline?.length
            ? [
                {
                  type: "spline",
                  data: baseline,
                  showInLegend: true,
                  name: "Energy Baseline",
                  color: "#ff0000",
                },
              ]
            : []),
        ]
        : [],
  };

  return (
    <div>
      {seriesData?.length ? (
        <HighchartsReact highcharts={Highcharts} options={chartOptions} />
      ) : (
        <div
          style={{
            height: "300px",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#D9D9D9",
            fontSize: "22px",
            fontWeight: "600",
            color: "#6d6d6d",
          }}
        >
          No Data Available
        </div>
      )}
    </div>
  );
};

export default React.memo(CustomSplineChart);
